import { useState } from "react"
import { Stage, StatePill } from "./Stage"
import { SubStageRow } from "./StagePrimitives"
import { PhoneCall } from "lucide-react"
import { APCMState } from "../../types/apcm-ops.types"
import type { ProgramStatus } from "../../types/outreach-program.types"
import * as api from "../../api/apcm-ops.api"
import { ManualRunAction } from "../program/ManualRunAction"

interface Props {
  orgId: string
  stateCounts: Record<string, number>
  stuckScheduledCount: number
  /** Patients still in CARE_PLAN_UPLOADED / CHECK_IN_NEEDED who already have a call queued. */
  alreadyScheduled: number
  onMarkScheduled: () => Promise<void>
  onRefresh: () => void
  onViewPatients: (states: string[]) => void
  /** The clinic program (null when not loaded); a disabled program makes "Start N" a manual run. */
  program: ProgramStatus | null
}

export function CheckInStage({
  orgId,
  stateCounts,
  stuckScheduledCount,
  alreadyScheduled,
  onMarkScheduled,
  onRefresh,
  onViewPatients,
  program,
}: Props) {
  const [marking, setMarking] = useState(false)

  const uploaded = stateCounts[APCMState.CARE_PLAN_UPLOADED] || 0
  const needed = stateCounts[APCMState.CHECK_IN_NEEDED] || 0
  const scheduled = stateCounts[APCMState.CHECK_IN_SCHEDULED] || 0
  const completed = stateCounts[APCMState.CHECK_IN_COMPLETED] || 0
  const noAnswer = stateCounts[APCMState.CHECK_IN_NO_ANSWER] || 0
  const cancelled = stateCounts[APCMState.CHECK_IN_CANCELLED] || 0
  const maxRetries = stateCounts[APCMState.CHECK_IN_MAX_RETRIES_REACHED] || 0

  const eligible = uploaded + needed
  const total = eligible + scheduled + completed + noAnswer + cancelled + maxRetries

  return (
    <Stage
      number={4}
      accent="checkIn"
      icon={PhoneCall}
      title="Check-in"
      description="Monthly check-in calls for patients with uploaded care plans. A new cycle starts each Central calendar month."
      total={total}
    >
      <ManualRunAction
        noun="check-in"
        eligible={eligible}
        readyLabel="ready for a check-in call"
        emptyLabel="no patients ready for check-in"
        helpText="Patients in CARE_PLAN_UPLOADED or CHECK_IN_NEEDED. Runs the clinic program for this many patients now: at most 4 attempts per Central month, 7 days apart, and a completed check-in ends the month's cycle."
        program={program}
        run={(limit) => api.initiateConsentedCheckin(orgId, limit)}
        onDone={onRefresh}
      />

      {alreadyScheduled > 0 && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-2">
          <SubStageRow
            label="already have a check-in call queued but are not marked CHECK_IN_SCHEDULED"
            count={alreadyScheduled}
            tone="warning"
            helpText="outcomes only record from CHECK_IN_SCHEDULED"
            actionLabel="Mark scheduled"
            loading={marking}
            onAction={async () => {
              setMarking(true)
              try {
                await onMarkScheduled()
              } finally {
                setMarking(false)
              }
            }}
          />
        </div>
      )}

      {(scheduled > 0 || completed > 0 || noAnswer > 0 || cancelled > 0 || maxRetries > 0) && (
        <div className="rounded-xl border bg-muted/20 px-4 py-2">
          {scheduled > 0 && (
            <SubStageRow
              label="scheduled — call in flight"
              count={scheduled}
              tone={stuckScheduledCount > 0 ? "warning" : "running"}
              helpText={
                stuckScheduledCount > 0
                  ? `⚠ ${stuckScheduledCount} stuck > 24h — check scheduler`
                  : undefined
              }
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CHECK_IN_SCHEDULED])}
            />
          )}
          {completed > 0 && (
            <SubStageRow
              label="completed this cycle"
              count={completed}
              tone="success"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CHECK_IN_COMPLETED])}
            />
          )}
          {noAnswer > 0 && (
            <SubStageRow
              label="no answer — may retry"
              count={noAnswer}
              tone="warning"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CHECK_IN_NO_ANSWER])}
            />
          )}
          {maxRetries > 0 && (
            <SubStageRow
              label="out of attempts this month"
              count={maxRetries}
              tone="warning"
              helpText="4 attempts used; the next month starts a new cycle"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CHECK_IN_MAX_RETRIES_REACHED])}
            />
          )}
          {cancelled > 0 && (
            <SubStageRow
              label="cancelled"
              count={cancelled}
              tone="neutral"
              actionLabel="View"
              onAction={() => onViewPatients([APCMState.CHECK_IN_CANCELLED])}
            />
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 pt-1">
        <StatePill label="CHECK_IN_NEEDED" count={needed} tone="amber" />
        {scheduled > 0 && <StatePill label="CHECK_IN_SCHEDULED" count={scheduled} tone="blue" />}
        {completed > 0 && (
          <StatePill label="CHECK_IN_COMPLETED" count={completed} tone="emerald" />
        )}
        {noAnswer > 0 && <StatePill label="CHECK_IN_NO_ANSWER" count={noAnswer} tone="amber" />}
        {cancelled > 0 && (
          <StatePill label="CHECK_IN_CANCELLED" count={cancelled} tone="neutral" />
        )}
        {maxRetries > 0 && (
          <StatePill label="CHECK_IN_MAX_RETRIES_REACHED" count={maxRetries} tone="red" />
        )}
      </div>
    </Stage>
  )
}
